/**
 * Loads the OneTrust banner and configures callbacks to react to privacy changes.
 */

import Log from "@/utils/Log";
import { ScriptLoader } from "@/utils/ScriptLoader";

export interface WindowWithOneTrust extends Window {
	/**
	 * A callback that is called by OneTrust, whenever `OnetrustActiveGroups` are updated, for example by a user action in the privacy center.
	 */
	OptanonWrapper?: () => void
	/**
	 *	Contains the currently active cookie groups, set by OneTrust.
	 */
	OnetrustActiveGroups?: string
	/**
	 * OneTrust object with methods for controlling the privacy center
	 */
	OneTrust?: {
		ToggleInfoDisplay: () => void
		[key: string]: any
	}
}

declare const window: WindowWithOneTrust;

/**
 * Defined by OneTrust. While these are editable in OneTrust, these are the defaults which we are sticking to.
 */
export enum OneTrustCookieGroup {
	StrictlyNecessary = "C0001",
	Performance = "C0002",
	Functionality = "C0003",
	Targeting = "C0004",
	SocialMedia = "C0005",
}

export default class OneTrust {
	currentActiveCookieGroups: OneTrustCookieGroup[] = [];

	public constructor(onActiveGroupsChanged: ((activeCookieGroups: OneTrustCookieGroup[], previousActiveCookieGroups: OneTrustCookieGroup[]) => void)) {
		window.OptanonWrapper = () => {
			const activeCookieGroups = OneTrust.getOneTrustActiveCookieGroups();
			onActiveGroupsChanged(activeCookieGroups, this.currentActiveCookieGroups);
			this.currentActiveCookieGroups = activeCookieGroups;
		};
	}

	public load(domainScript: string): void {
		// while OneTrust recommends to load this script as the very first thing inside head, this is only necessary if you want to use their automagic features to control
		// script loading based on markup. We do this ourselves anyway, and mostly use OneTrust to load the banner and privacy center.
		ScriptLoader.LoadScript("https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
			{ "data-domain-script": domainScript });
	}

	public static getOneTrustActiveCookieGroups(): OneTrustCookieGroup[] {
		if (window.OnetrustActiveGroups == null) {
			Log.error("window.OneTrustActiveGroups accessed before initialization.");
			return [];
		}

		return window.OnetrustActiveGroups
			.split(",")
			.filter(Boolean)
			.filter((value) => Object.values(OneTrustCookieGroup).includes(value as OneTrustCookieGroup))
			.map((activeGroup) => activeGroup as OneTrustCookieGroup);
	}

	/**
	 * Opens the OneTrust privacy center
	 * Used for "Do Not Sell Or Share My Personal Information" link
	 */
	public static openPrivacyCenter(): void {
		if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === "function") {
			window.OneTrust.ToggleInfoDisplay();
		}
		else {
			console.warn("OneTrust privacy center not available yet");
		}
	}
}
